<template>
  <div class="display">
    <iframe
      ref="previewIframe"
      :src="iframeSrc"
      title="live display preview"
      class="poll-display-iframe"
      data-test="poll-display-iframe"
      @load="frameLoaded = true"
    />
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

import Poll from '../../services/poll';
import { PAGES_URL } from '../../services/services';

export default {
  name: 'Poll',
  props: {
    displayData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      iframeSrc: `${PAGES_URL}/display/live-output`,
      pollData: null,
      PAGES_URL,
      frameLoaded: false,
    };
  },

  created() {
    this.fetchPoll();
  },

  mounted() {
    window.addEventListener('message', this.onMessage);
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },

  watch: {
    displayData: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.frameLoaded) {
          this.sendPreviewData();
        }
      },
    },

    actions: {
      deep: true,
      handler(actions) {
        if (actions[0]?.type === 'poll') {
          this.$refs.previewIframe.contentWindow?.postMessage({
            type: 'tgbLive:actions',
            data: actions[0].value,
          }, '*');
        }
      },
    },
  },

  computed: {
    actions() {
      return this.$store.state.production.displayActions;
    },

    pollStage() {
      return this.displayData.currentDisplayState || 'results';
    },

    roundIndex() {
      return this.displayData.currentRoundIndex || 0;
    },

    questionIndex() {
      return this.displayData.currentQuestionIndex || 0;
    },

    isInteractive() {
      return this.displayData.displayType === 'poll' || false;
    },

    animateResults() {
      return this.displayData.animateResults || false;
    },

    renderData() {
      return {
        display: {
          elements: [
            {
              type: 'poll',
              poll: {
                ...this.pollData,
                // if the poll is interactive, we want it to behave like a live poll
                // so don't send the editor state (it needs to be undefined)
                editorState: this.isInteractive ? undefined : {
                  pollStage: this.pollStage,
                  roundIndex: this.roundIndex,
                  questionIndex: this.questionIndex,
                  animateResults: this.animateResults,
                },
              },
              settings: {
                visible: true,
                displayId: this.displayData.displayId,
              },
            },
          ],
        },
        fonts: this.pollData?.fonts ?? [],
        // display has clickable elements so allow interaction
        interactive: this.isInteractive,
      };
    },
  },

  methods: {
    onMessage(e) {
      switch (e.data.type) {
        case 'display-preview-ready': {
          // Once the preview is loaded...
          this.sendPreviewData();
          break;
        }

        case 'display-update-received': {
          // Once the preview ack's our display data...
          this.$emit('frameloaded');
          break;
        }

        default: {
          // noop / unsupported type
          break;
        }
      }
    },

    async fetchPoll() {
      try {
        this.pollData = await Poll.getPoll(this.displayData.pollId);
        this.sendPreviewData();
      } catch (error) {
        console.error('Error fetching poll data:', error);
      }
    },

    sendPreviewData: debounce(function sendPreviewData() {
      if (this.pollData && this.$refs.previewIframe) {
        const plainData = JSON.parse(JSON.stringify(this.renderData));
        this.$refs.previewIframe.contentWindow?.postMessage({
          type: 'display-builder-update',
          data: plainData,
        }, '*');
      }
    }, 250),
  },
};
</script>

<style lang="scss" scoped>
.poll-display-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
